import { NUTRIENT_KEYS } from '../../../lib/constants/constants';

/**
 * Calculates scores for nutrient quality indicators
 * @param {Object} item - The nutrient data object containing current and goal values
 * @returns {Object} An object containing currentScore, goalScore, and their derived indicators
 */
export const calculateNutrientScores = (item) => {
  if (!item || !item.current || !item.goal) {
    return {
      currentScore: 0,
      goalScore: 0,
      showOnGreen: false,
      showOnRed: false,
    };
  }

  // Calculate score based on how close current is to goal
  // Score range is 1-10, where 10 is perfect match
  const current = Number(item.current);
  const goal = Number(item.goal);

  // For nutrients that should be limited (like added sugars, saturated fat)
  const isLimitNutrient = item.key ? Object.values(NUTRIENT_KEYS.nutrients).includes(item.key) : false;

  let currentScore;
  if (isLimitNutrient) {
    // For limit nutrients, lower is better
    // If current is 0, score is 10
    // If current is >= goal, score is 1
    // Linear scale in between
    const ratio = current / goal;
    currentScore = Math.max(1, Math.min(10, Math.round(10 - ratio * 9)));
  } else {
    // For other nutrients, higher is better
    // If current is 0, score is 1
    // If current is >= goal, score is 10
    // Linear scale in between
    const ratio = current / goal;
    currentScore = Math.max(1, Math.min(10, Math.round(1 + ratio * 9)));
  }

  // Goal score is always 1 point higher than current, up to 10
  const goalScore = Math.min(10, currentScore + 1);

  // For limit nutrients, we invert which slider shows the indicator
  // If we're at 50% of the limit (score = 6), we want to show on red
  // If we're at 0% of the limit (score = 10), we want to show on green
  // If we're at 100% of the limit (score = 1), we want to show on red
  return {
    currentScore,
    goalScore,
    showOnGreen: isLimitNutrient ? currentScore >= 9 : currentScore > 5,
    showOnRed: isLimitNutrient ? currentScore < 9 : currentScore <= 5,
  };
};

/**
 * Calculates the change between current and goal values
 * @param {number} current - Current value
 * @param {number} goal - Goal value
 * @param {string} unit - Unit of measurement
 * @returns {Object} An object containing the change value and its unit
 */
export const calculateNutrientChange = (current, goal, unit) => {
  // Check if inputs are valid numbers
  if (isNaN(current) || isNaN(goal)) {
    return { value: '', unit };
  }

  const value = goal - current;
  if (isNaN(value)) {
    return { value: '', unit };
  }

  const prefix = value > 0 ? '+' : '';
  return {
    value: value === 0 ? value : `${prefix}${value}`,
    unit,
  };
};
